import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Container,
  InputAdornment,
  InputBase,
  List,
  ListItemButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { formatDistanceToNowStrict } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import { useConversations } from './useConversation';
import { avatarColor, initials, isActiveNow } from './chatVisuals';

/**
 * Inbox page — Instagram/WhatsApp-style list of conversations. Selecting a
 * thread navigates to a dedicated /messages/:conversationId page rather than
 * cramming a split-pane into mobile viewports.
 */
export function InboxPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('fr') ? frLocale : undefined;
  const navigate = useNavigate();
  const { conversations, isLoading } = useConversations();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkId = searchParams.get('conversationId');
  const [query, setQuery] = useState('');

  // Honor legacy ?conversationId=X deep-links by redirecting to the new
  // /messages/:id route once the conversation appears in the loaded list.
  useEffect(() => {
    if (!deepLinkId) return;
    const exists = conversations.some((c) => c.id === deepLinkId);
    if (!exists) return;
    const next = new URLSearchParams(searchParams);
    next.delete('conversationId');
    setSearchParams(next, { replace: true });
    navigate(`/messages/${deepLinkId}`, { replace: true });
  }, [deepLinkId, conversations, searchParams, setSearchParams, navigate]);

  const visibleConversations = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => {
      const label = (c.otherParticipant.businessName ?? c.otherParticipant.name).toLowerCase();
      const preview = (c.lastMessagePreview ?? '').toLowerCase();
      return label.includes(q) || preview.includes(q);
    });
  }, [conversations, query]);

  const totalUnread = useMemo(
    () => conversations.reduce((sum, c) => sum + (c.unreadCount > 0 ? 1 : 0), 0),
    [conversations],
  );

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, md: 5 }, px: { xs: 0, sm: 2, md: 3 } }}>
      <Box sx={{ px: { xs: 2, sm: 0 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, letterSpacing: -0.5, fontSize: { xs: 26, md: 34 } }}
          >
            {t('chat.inboxTitle')}
          </Typography>
          {totalUnread > 0 && (
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: '#22C55E',
                boxShadow: '0 0 0 4px rgba(34,197,94,0.15)',
              }}
            />
          )}
        </Stack>
        <Typography
          color="text.secondary"
          sx={{ mb: { xs: 2, md: 3 }, fontSize: { xs: 13, md: 15 } }}
        >
          {t('chat.inboxSubtitle')}
        </Typography>
      </Box>

      <Box
        sx={{
          border: { xs: 'none', sm: '1px solid' },
          borderColor: 'divider',
          borderRadius: { xs: 0, sm: 4 },
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: { sm: '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.04)' },
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <InputBase
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('chat.searchPlaceholder', 'Rechercher')}
            fullWidth
            startAdornment={
              <InputAdornment position="start" sx={{ ml: 0.5, color: 'text.disabled' }}>
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            }
            sx={{
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#F0F2F5',
              borderRadius: 999,
              px: 1.5,
              py: 0.5,
              fontSize: 14,
              '& input': { py: 0.75 },
            }}
          />
        </Box>

        {isLoading ? (
          <Stack spacing={1} sx={{ p: 2 }}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={64} />
            ))}
          </Stack>
        ) : conversations.length === 0 ? (
          <EmptyInbox />
        ) : visibleConversations.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('chat.searchNoResults', 'Aucune conversation trouvée.')}
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ py: 0.5 }}>
            {visibleConversations.map((c) => {
              const otherLabel = c.otherParticipant.businessName ?? c.otherParticipant.name;
              const timeAgo = c.lastMessageAt
                ? formatDistanceToNowStrict(new Date(c.lastMessageAt), { locale: dateLocale })
                : null;
              const preview = c.lastMessagePreview ?? t('chat.noMessagesYet');
              const subtitle = timeAgo ? `${preview} · ${timeAgo}` : preview;
              const isUnread = c.unreadCount > 0;
              const isActiveNow_ = isActiveNow(c.lastMessageAt);
              return (
                <ListItemButton
                  key={c.id}
                  onClick={() => navigate(`/messages/${c.id}`)}
                  sx={{
                    px: 2,
                    py: 1.25,
                    gap: 1.5,
                    borderRadius: 2,
                    mx: 1,
                    width: 'auto',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      flex: '0 0 auto',
                      ...(isActiveNow_ && {
                        p: '2px',
                        borderRadius: '50%',
                        background:
                          'linear-gradient(135deg, #25F4EE 0%, #FE2C55 100%)',
                      }),
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: avatarColor(c.otherParticipant.userId),
                        color: 'common.white',
                        fontSize: 18,
                        fontWeight: 600,
                        border: isActiveNow_ ? '2px solid' : 'none',
                        borderColor: 'background.paper',
                      }}
                    >
                      {initials(otherLabel)}
                    </Avatar>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: isUnread ? 700 : 600,
                        fontSize: 15,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: 'text.primary',
                      }}
                    >
                      {otherLabel}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: isUnread ? 'text.primary' : 'text.secondary',
                        fontWeight: isUnread ? 500 : 400,
                        fontSize: 13,
                      }}
                    >
                      {subtitle}
                    </Typography>
                  </Box>
                  {isUnread && (
                    <Box
                      sx={{
                        flex: '0 0 auto',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: '#FF3B30',
                      }}
                    />
                  )}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Container>
  );
}

function EmptyInbox() {
  const { t } = useTranslation();
  return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
      <Typography variant="body2" color="text.secondary">
        {t('chat.inboxEmpty')}
      </Typography>
    </Box>
  );
}
